 
/**
 * S17 · Syncing — live progress view that animates while the queue
 * drains. Polls the rollup + pending lists every 500 ms so the progress
 * wheel feels like a live stream even though it's backed by SQLite.
 */
import { useEffect, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import { useDrainMutation, usePendingSyncTasks, useSyncRollup, useSyncStatus } from '@kapture/client-core';

export const Route = createFileRoute('/sync/active')({
  component: SyncingPage,
  staticData: { mobile: true },
});

function SyncingPage() {
  const navigate = useNavigate();
  const { online } = useSyncStatus();
  const { data: rollup } = useSyncRollup(500);
  const { data: pending } = usePendingSyncTasks(500);
  const drainMut = useDrainMutation();

  // Auto-trigger a drain if we land here with live pending tasks and
  // nothing is in flight — matches the /sync "Sync now" flow.
  useEffect(() => {
    if (online && !drainMut.isPending && (rollup?.queued ?? 0) > 0) {
      void drainMut.mutateAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, rollup?.queued]);

  const total =
    (rollup?.queued ?? 0) +
    (rollup?.retrying ?? 0) +
    (rollup?.uploading ?? 0) +
    (rollup?.succeeded ?? 0) +
    (rollup?.conflict ?? 0) +
    (rollup?.failed ?? 0);
  const done = rollup?.succeeded ?? 0;
  const progress = total === 0 ? 0 : Math.min(1, done / total);
  const inFlight = useMemo(
    () => (pending ?? []).find((t) => t.status === 'uploading'),
    [pending],
  );

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <button
          type="button"
          onClick={() => navigate({ to: '/sync' })}
          aria-label="Back"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: T.text,
            display: 'flex',
          }}
        >
          <Ico d={I.back} size={20} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Syncing</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {online ? 'Pushing your changes upstream' : 'Offline — retry when signal returns'}
          </div>
        </div>
        {drainMut.isPending ? (
          <Chip tone="info" size="sm">
            Live
          </Chip>
        ) : (
          <Chip tone="neutral" size="sm">
            Idle
          </Chip>
        )}
      </div>

      <div
        style={{
          padding: 20,
          margin: 16,
          borderRadius: 18,
          background: `linear-gradient(135deg, ${T.brand} 0%, ${T.brandDark} 100%)`,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
          boxShadow: '0 10px 30px rgba(255,90,31,0.25)',
        }}
      >
        <ProgressWheel progress={progress} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: T.font, letterSpacing: '-0.01em' }}>
            {done} of {total} synced
          </div>
          <div
            style={{
              fontSize: 11,
              fontFamily: T.mono,
              opacity: 0.85,
              marginTop: 3,
              letterSpacing: '0.04em',
            }}
          >
            {progress === 1
              ? 'All caught up'
              : inFlight
                ? `Uploading · ${inFlight.id.slice(0, 8)}`
                : drainMut.isPending
                  ? 'Replaying queue…'
                  : 'Waiting for a push'}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        <div
          style={{
            fontSize: 10,
            color: T.textMute,
            fontFamily: T.mono,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 8,
          }}
        >
          Recent activity
        </div>
        {(pending ?? []).map((t) => (
          <div
            key={t.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              border: `1px solid ${T.stroke}`,
              borderRadius: 10,
              background: T.surface,
              marginBottom: 6,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: 4,
                background:
                  t.status === 'uploading'
                    ? T.brand
                    : t.status === 'conflict'
                      ? T.warn
                      : t.status === 'failed'
                        ? T.danger
                        : T.stroke2,
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, fontFamily: T.font }}>
                {t.op} · {t.entityId.slice(0, 8)}
              </div>
              {t.lastError && (
                <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font, marginTop: 2 }}>
                  {t.lastError}
                </div>
              )}
            </div>
            <Chip
              tone={
                t.status === 'succeeded'
                  ? 'good'
                  : t.status === 'failed'
                    ? 'danger'
                    : t.status === 'conflict'
                      ? 'warn'
                      : 'neutral'
              }
              size="sm"
            >
              {t.status}
            </Chip>
          </div>
        ))}
        {(pending ?? []).length === 0 && (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: T.textDim,
              fontFamily: T.font,
              fontSize: 13,
            }}
          >
            No pending tasks — everything is in.
          </div>
        )}
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          icon={<Ico d={I.refresh} size={14} />}
          onClick={() => void drainMut.mutateAsync()}
          disabled={!online || drainMut.isPending}
          style={{ opacity: !online ? 0.5 : 1 }}
        >
          Drain again
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.arrow} size={16} />}
          onClick={() => navigate({ to: '/sync' })}
        >
          Back to queue
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function ProgressWheel({ progress }: { progress: number }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - progress);
  return (
    <div style={{ position: 'relative', width: 96, height: 96 }}>
      <svg viewBox="0 0 96 96" width={96} height={96}>
        <circle
          cx={48}
          cy={48}
          r={r}
          stroke="rgba(255,255,255,0.25)"
          strokeWidth={8}
          fill="none"
        />
        <circle
          cx={48}
          cy={48}
          r={r}
          stroke="#fff"
          strokeWidth={8}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform="rotate(-90 48 48)"
          style={{ transition: 'stroke-dashoffset 300ms ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          fontFamily: T.mono,
          fontSize: 20,
          fontWeight: 700,
          color: '#fff',
        }}
      >
        {Math.round(progress * 100)}%
      </div>
    </div>
  );
}
